import classNames from "classnames"
import { useEffect, useRef, useState } from "react"
import { useAppSelector } from "@/store/hooks"

/**
 * The turn-limit counter, plus the card that explains it on hover. Positioned
 * by its parent (Board stacks it under the turn timer in the top-right corner
 * of the center square, clear of the rolling dice).
 *
 * This is the most misread number on the board. It counts full rounds — one of
 * these is every player playing once — not the turns any single player has
 * left, and nothing else on screen says what actually happens when it reaches
 * zero. So the card spells out both, and in particular that the game is decided
 * on net worth rather than just stopping, before players find that out the hard
 * way on the last turn.
 *
 * A max-turns setting of 0 means "no limit", which arrives here as Infinity —
 * the counter says so instead of rendering the word "Infinity".
 */
const TurnsLeftBadge: React.FC = () => {
  const turnsLeft = useAppSelector((state) => state.global.myGame?.situation?.turnsLeft)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  // Tapping pins the card open — hover alone is useless on touch screens.
  const [pinned, setPinned] = useState(false)
  const open = hovered || pinned

  // A pinned card is dismissed by tapping/clicking anywhere else.
  useEffect(() => {
    if (!pinned) return
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null
      if (target && wrapperRef.current?.contains(target)) return
      setPinned(false)
    }
    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("touchstart", handlePointerDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("touchstart", handlePointerDown)
    }
  }, [pinned])

  if (turnsLeft === undefined || turnsLeft === null) return null

  const unlimited = !isFinite(turnsLeft)
  const isFinalTurn = !unlimited && turnsLeft <= 1
  const isEndingSoon = !unlimited && turnsLeft <= 5

  // Matches the endgame toast: amber while the limit is only approaching, red
  // once this is the turn the game ends on.
  const tone = isFinalTurn
    ? {
        badge: "bg-gradient-to-r from-[#e0282b] to-[#a50f12] text-white ring-black/20",
        icon: "fa-flag-checkered",
        accent: "#ff6b6b",
        accentSoft: "rgba(255,107,107,0.16)",
      }
    : isEndingSoon
    ? {
        badge: "bg-gradient-to-r from-[#f0a02b] to-[#d97012] text-[#2e1503] ring-black/20",
        icon: "fa-hourglass-half",
        accent: "#f5b451",
        accentSoft: "rgba(245,180,81,0.14)",
      }
    : {
        badge: "bg-gradient-slider-thumb text-[#3d2408] ring-black/10",
        icon: unlimited ? "fa-infinity" : "fa-hourglass-half",
        accent: "#e8b055",
        accentSoft: "rgba(232,176,85,0.14)",
      }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="Turns left — hover for details"
        data-testid="game.turns-left-badge"
        onClick={(e) => {
          e.stopPropagation()
          setPinned((prev) => !prev)
        }}
        className={classNames(
          // Deliberately narrow: it hangs over the middle of the board where the
          // dice tumble, so every bit of width is a bit more overlap.
          "flex items-center gap-1.5 rounded-full px-2.5 py-1 font-poppins whitespace-nowrap cursor-help select-none ring-1 shadow-lg transition-transform duration-150",
          tone.badge,
          { "turns-left-badge-final": isFinalTurn, "scale-105": open }
        )}
      >
        <i className={classNames("fa-solid text-xs opacity-80", tone.icon)}></i>
        {unlimited ? (
          <span className="text-xs font-bold uppercase tracking-[0.08em] leading-none">
            No limit
          </span>
        ) : (
          <>
            <span className="text-base font-bold tabular-nums leading-none">{turnsLeft}</span>
            <span className="text-[0.625rem] font-bold uppercase tracking-[0.08em] leading-none">
              {turnsLeft === 1 ? "turn left" : "turns left"}
            </span>
          </>
        )}
        <i className="fa-solid fa-circle-info text-[0.625rem] opacity-60"></i>
      </div>

      {open && (
        // Anchored under the badge rather than portalled: the badge sits in the
        // top-right of the center square, so there is always room below it and
        // none of the sidebar clipping RoleBadge has to work around. Right-edge
        // aligned so a card this wide opens inward over the board instead of
        // off the side of it.
        <div className="turns-left-tip absolute top-full right-0 mt-3 w-[19rem] max-w-[70vw] z-10 pointer-events-none">
          <div
            className="relative rounded-xl border px-3.5 py-3 font-poppins backdrop-blur-md"
            style={{
              borderColor: tone.accent + "59",
              background: "linear-gradient(150deg, rgba(24,24,27,0.97) 0%, rgba(9,9,11,0.97) 100%)",
              boxShadow: `0 12px 32px rgba(0,0,0,0.65), 0 0 18px ${tone.accent}40`,
            }}
          >
            {/* Arrow pointing back up at the badge, near the right edge the
                card is anchored to. */}
            <div
              className="absolute right-8 -top-[5px] h-2.5 w-2.5 rotate-45 border-l border-t"
              style={{ borderColor: tone.accent + "59", background: "rgb(19,19,21)" }}
            ></div>

            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: tone.accent, boxShadow: `0 0 8px ${tone.accent}` }}
              ></span>
              <span
                className="text-[1.0625rem] font-bold leading-none tracking-wide"
                style={{ color: tone.accent }}
              >
                {unlimited
                  ? "No turn limit"
                  : isFinalTurn
                  ? "Final turn of the game"
                  : `${turnsLeft} turns left to play`}
              </span>
            </div>

            <div className="mt-2 text-[0.9375rem] leading-snug text-white/70">
              {unlimited ? (
                <>This game has no turn limit — it runs until only one player is left standing.</>
              ) : (
                <>
                  A <span className="font-semibold text-white/90">turn</span> here means one full
                  round: every player rolls once. This counts down by 1 each time the round comes
                  back to the first player.
                </>
              )}
            </div>

            {!unlimited && (
              <div
                className="mt-2.5 flex items-start gap-2 rounded-md px-2 py-2 text-[0.9375rem] leading-snug font-medium"
                style={{ backgroundColor: tone.accentSoft, color: tone.accent }}
              >
                <i className="fa-solid fa-trophy text-[0.75rem] mt-[4px] shrink-0"></i>
                <span>
                  At 0 the game ends and the player with the{" "}
                  <span className="font-bold">highest net worth wins</span> — cash + properties +
                  houses.
                </span>
              </div>
            )}

            <div className="mt-2 text-[0.8125rem] leading-snug text-white/50">
              Bankrupt everyone else before then and you win on the spot.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TurnsLeftBadge
